import React from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BLUEPRINT_ISO_PAPER_NAMES,
  type BlueprintIsoPaperName,
} from '@shared/helpers/design/blueprint-underlay.helpers';

type BlueprintPaperSizeSelectProps = {
  /** Current ISO name when the paper matches A0–A4; otherwise empty. */
  value: BlueprintIsoPaperName | undefined;
  /** Chip label — ISO name or `W × H mm` for custom paper. */
  label: string;
  disabled?: boolean;
  onChange: (name: BlueprintIsoPaperName) => void;
  'aria-label'?: string;
};

/**
 * Compact paper-size chip for underlay scale — pick A0–A4 while keeping `1:N`.
 */
export const BlueprintPaperSizeSelect: React.FC<
  BlueprintPaperSizeSelectProps
> = ({ value, label, disabled, onChange, 'aria-label': ariaLabel }) => {
  const { classes } = useStyles();

  return (
    <FormControl size="small" disabled={disabled} className={classes.control}>
      <Select
        value={value ?? ''}
        displayEmpty
        className={classes.select}
        aria-label={ariaLabel ?? 'Paper size'}
        renderValue={() => label}
        onChange={(event) => {
          const next = String(event.target.value);
          const matched = BLUEPRINT_ISO_PAPER_NAMES.find(
            (name) => name === next,
          );
          if (!matched) return;
          onChange(matched);
        }}
      >
        {BLUEPRINT_ISO_PAPER_NAMES.map((name) => (
          <MenuItem key={name} value={name} className={classes.menuItem}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const useStyles = makeStyles()((theme) => ({
  control: {
    width: 'auto',
    minWidth: 0,
    flex: '0 0 auto',
  },
  select: {
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.grey[200],
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 0,
      boxSizing: 'border-box',
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
      paddingLeft: theme.spacing(0.75),
      paddingRight: 22,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: theme.typography.body2.fontWeight,
      lineHeight: 1.35,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    },
    '& .MuiSelect-icon': {
      right: 2,
      fontSize: 18,
      color: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  menuItem: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
  },
}));
